'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useWindowSize } from 'usehooks-ts';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/solid';
import Link from 'next/link';
import Cookies from 'js-cookie';

import { LOGIN_COOKIE_KEY, LOGIN_COOKIE_DOMAIN, LoginStatus } from '@constants/general';

import AuthService from '@services/auth';
import UiUtils from '@utils/ui';
import { useGlobalContext } from '@context/global';
import useHash from '@hooks/use-hash';

import Logo from '@images/seedkit_logo_black.svg';
import ButtonV1 from '@components/button-v1';
import SpinnerButton from '@components/spinner-button';
import { AlertVariant } from '@/constants/alert';

export default function NavBar({ onAlertOpen }) {
    const { setContextValues } = useGlobalContext();

    const [menuOpen, setMenuOpen] = useState(false);

    const { width } = useWindowSize();
    const isMobile = UiUtils.isMobile(width);
    const isDesktop = !isMobile;

    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        if (window) {
            window.scroll(0, 0);
        }
    }, [pathname]);

    const [loginStatus, setLoginStatus] = useState(LoginStatus.UNKNOWN);

    const [loginSpinning, setLoginSpinning] = useState(false);
    const [signUpSpinning, setSignUpSpinning] = useState(false);
    const [logOutSpinning, setLogOutSpinning] = useState(false);

    /* Outage Check Code */

    const outage = process.env.NEXT_PUBLIC_OUTAGE && JSON.parse(process.env.NEXT_PUBLIC_OUTAGE) ? JSON.parse(process.env.NEXT_PUBLIC_OUTAGE) : false;

    if (outage) {
        router.replace('/outage');
    }

    useEffect(() => {
        setLoginStatus(AuthService.isLoggedIn() ? LoginStatus.LOGGED_IN : LoginStatus.LOGGED_OUT);
    }, []);

    function login() {
        if (!outage && pathname.indexOf("login") === -1) {
            setLoginSpinning(true);
            router.push('/login');
        }
    }

    function signUp() {
        if (!outage) {
            setSignUpSpinning(true);
            if (pathname.indexOf("signup") > -1) {
                if (window) {
                    window.location.reload();
                }
            } else {
                router.push('/signup');
            }
        } 
    }

    function clearContext() {
        setContextValues([
            { key: "user", value: {} },
            { key: "organisation", value: {} },
            { key: "authToken", value: "" },
            { key: "selections", value: {} }
        ]);
    };

    function logOut() {
        if (!outage) {
            setLogOutSpinning(true);
            clearContext();
            AuthService.logout()
            .then((_) => {
                Cookies.remove(LOGIN_COOKIE_KEY, { path: "/", domain: LOGIN_COOKIE_DOMAIN });
                setLoginStatus(LoginStatus.LOGGED_OUT);
                router.push('/');
            })
            .catch(error => console.log(error));
            return false;
        }
    }

    function toggleMenu() {
        if (!outage) {
            setMenuOpen(!menuOpen);
        }
    }

    function doNavigate(link) {
        if (!outage) {
            router.push(link);
        }
    }

    function accountClick() {
        if (!outage) {
            if (loginStatus === LoginStatus.LOGGED_IN) {
                doNavigate("/kit/home");
            } else {
                if (pathname.indexOf("login") > -1) {
                    if (onAlertOpen) {
                        onAlertOpen({
                            variant: AlertVariant.WARNING,
                            title: "Login required",
                            text: "You will need to login to access this section of Seedkit."
                        });
                    }
                } else {
                    login();
                }
            }
        }
    }

    /* Hash Navigation Handling Functions */

    const hash = useHash();

    useEffect(() => {
        const section = hash.replace("#", "");
        if (section) {
            UiUtils.scrollToSection(section);
        }
    }, [hash]);

    return (
        <>
            {isDesktop && (
                <div
                    role="navigation"
                    className="flex justify-between items-center box-border max-w-[1312px] w-full h-16 px-6 rounded-xl bg-white shadow-standard z-50 mx-auto print:hidden"
                >
                    <Link href="/">
                        <Image 
                            src={Logo}
                            width={90}
                            height={26} 
                            className="h-[26px]"
                            alt="Seedkit logo" 
                        />
                    </Link>
                    <div className="flex items-center justify-center">
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="About" 
                            disabled={outage}
                            onClick={() => doNavigate("/about")}
                        />
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="Resources" 
                            disabled={outage}
                            onClick={() => doNavigate("/resources")}
                        />
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="Contact" 
                            disabled={outage}
                            onClick={() => doNavigate("/contact")}
                        />
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="Account"
                            disabled={outage} 
                            onClick={() => accountClick()}
                        />
                    </div>
                    <div className="flex items-center justify-center gap-3">
                        {loginStatus === LoginStatus.LOGGED_IN && (
                            <SpinnerButton 
                                size="small" 
                                variant="outline" 
                                label="Log out" 
                                spinning={logOutSpinning}
                                disabled={outage} 
                                onClick={logOut}
                            />
                        )}
                        {loginStatus === LoginStatus.LOGGED_OUT && (
                            <>
                                <SpinnerButton 
                                    size="small" 
                                    variant="outline" 
                                    label="Sign up" 
                                    spinning={signUpSpinning}
                                    disabled={outage} 
                                    onClick={signUp}
                                />
                                <SpinnerButton 
                                    size="small" 
                                    variant="solid" 
                                    label="Login" 
                                    spinning={loginSpinning}
                                    disabled={outage} 
                                    onClick={login}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
            {isMobile && (
                <div
                    role="navigation"
                    className="block box-border w-full rounded-xl bg-white shadow-standard z-50 print:hidden"
                >
                    <div className="box-border w-full h-50px p-3 flex items-center justify-between">
                        <Link href="/">
                            <Image 
                                src={Logo} 
                                width={86}
                                height={25}
                                className="h-[25px]"
                                alt="Seedkit logo" 
                            />
                        </Link>
                        {menuOpen && (
                            <XMarkIcon 
                                className="h-6 text-black cursor-pointer" 
                                aria-label="Close nav menu"
                                onClick={toggleMenu}
                            />
                        )}
                        {!menuOpen && (
                            <Bars3Icon 
                                className="h-6 text-black cursor-pointer" 
                                aria-label="Open nav menu"
                                onClick={toggleMenu}
                            />
                        )}
                    </div>
                    <div className={menuOpen ? "box-border w-full px-4 pt-3 pb-6" : "hidden"}>
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="About" 
                            className="pl-0"
                            disabled={outage} 
                            onClick={() => doNavigate("/about")}
                        />
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="Resources" 
                            className="pl-0"
                            disabled={outage} 
                            onClick={() => 
                            doNavigate("/resources")}
                        />
                        <ButtonV1 
                            size="small" 
                            variant="navlink" 
                            label="Contact" 
                            className="pl-0"
                            disabled={outage} 
                            onClick={() => 
                            doNavigate("/contact")}
                        />
                        <div className="flex w-full mt-3 gap-3 justify-stretch">
                            {loginStatus === LoginStatus.LOGGED_IN && (
                                <SpinnerButton 
                                    size="small" 
                                    variant="outline" 
                                    label="Log out" 
                                    spinning={logOutSpinning}
                                    disabled={outage} 
                                    onClick={logOut}
                                />
                            )}
                            {loginStatus === LoginStatus.LOGGED_OUT  && (
                                <>
                                    <SpinnerButton 
                                        size="small" 
                                        variant="outline" 
                                        label="Sign up" 
                                        spinning={signUpSpinning}
                                        disabled={outage} 
                                        onClick={signUp}
                                    />
                                    <SpinnerButton 
                                        size="small" 
                                        variant="solid" 
                                        label="Login" 
                                        spinning={loginSpinning}
                                        disabled={outage} 
                                        onClick={login}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}